import React, { useEffect, useRef, useState } from 'react';

const Footer: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [sectionsVisible, setSectionsVisible] = useState([false, false, false]);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate sections with stagger
          setTimeout(() => setSectionsVisible([true, false, false]), 200);
          setTimeout(() => setSectionsVisible([true, true, false]), 400);
          setTimeout(() => setSectionsVisible([true, true, true]), 600);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer ref={sectionRef} className="bg-prussian-blue text-white overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 py-12 md:py-20 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-12 max-w-6xl mx-auto">
          {/* Logo and description */}
          <div className={`md:col-span-2 transition-all duration-1000 ${
            sectionsVisible[0] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
          }`}>
            <div className="text-2xl md:text-4xl font-bold text-white mb-3 md:mb-6 hover:text-alice-blue transition-colors duration-300 cursor-default">
              المميز<span className="text-celestial-blue">.</span>
            </div>
            <p className="text-alice-blue/80 mb-4 md:mb-8 leading-relaxed text-sm md:text-lg max-w-lg hover:text-alice-blue transition-colors duration-300">
              مدرسة المميز لتعليم القيادة - نصنع منكِ قائدة واثقة وماهرة. 
              نقدم أفضل التدريب بأحدث الطرق وأكثرها أماناً.
            </p>
            <div className="flex space-x-4 space-x-reverse">
              {[
                { icon: '📱', href: '#' },
                { icon: '💬', href: '#' },
                { icon: '📧', href: '#' }
              ].map((social, index) => (
                <a 
                  key={index}
                  href={social.href} 
                  className="text-alice-blue/80 hover:text-celestial-blue transition-all duration-300 transform hover:scale-125 hover:-translate-y-1"
                >
                  <span className="text-xl md:text-3xl">{social.icon}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className={`transition-all duration-1000 delay-200 ${
            sectionsVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h3 className="text-base md:text-xl font-bold mb-3 md:mb-6 text-celestial-blue">روابط سريعة</h3>
            <ul className="space-y-2 md:space-y-3">
              {[
                { text: 'فلسفتنا', section: 'philosophy' },
                { text: 'الحصص', section: 'journey' },
                { text: 'المتطلبات', section: 'requirements' },
                { text: 'اتصل بنا', section: 'contact' }
              ].map((link, index) => (
                <li key={index}>
                  <button 
                    onClick={() => scrollToSection(link.section)}
                    className="text-alice-blue/80 hover:text-celestial-blue transition-all duration-300 text-sm md:text-lg transform hover:translate-x-2 hover:scale-105"
                  >
                    {link.text}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact info */}
          <div className={`transition-all duration-1000 delay-400 ${
            sectionsVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h3 className="text-base md:text-xl font-bold mb-3 md:mb-6 text-celestial-blue">معلومات التواصل</h3>
            <ul className="space-y-2 md:space-y-3 text-alice-blue/80 text-sm md:text-lg">
              {[
                { icon: '📞', text: ' 971509374446+' },
                { icon: '📧', text: 'info@almumayaz.ae' },
                { icon: '📍', text: 'عجمان الروضة، الإمارات' },
                { icon: '🕒', text: '4:00 ص - 8:00 م' }
              ].map((contact, index) => (
                <li key={index} className="flex items-center group">
                  <span className="text-celestial-blue mr-2 text-sm md:text-lg transform transition-all duration-300 group-hover:scale-125">
                    {contact.icon}
                  </span>
                  <span className={`transition-colors duration-300 group-hover:text-white ${
                    contact.text.includes('@') ? 'break-all' : ''
                  }`}>
                    {contact.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom section */}
        <div className={`border-t border-lapis-lazuli/30 mt-8 md:mt-16 pt-6 md:pt-10 transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 max-w-6xl mx-auto">
            <p className="text-alice-blue/80 text-xs md:text-base text-center md:text-right hover:text-alice-blue transition-colors duration-300">
              &copy; 2025 مدرسة المميز | جميع الحقوق محفوظة
            </p>
            <div className="flex space-x-4 md:space-x-8 space-x-reverse">
              <a href="#" className="text-alice-blue/80 hover:text-celestial-blue text-xs md:text-base transition-all duration-300 transform hover:scale-105">
                سياسة الخصوصية
              </a>
              <a href="#" className="text-alice-blue/80 hover:text-celestial-blue text-xs md:text-base transition-all duration-300 transform hover:scale-105">
                الشروط والأحكام
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 